import { extractNotionIdFromFilename, normalizeExportPath } from '@couplespace/core/notion-export';

import { newBlockId } from '@/lib/blocks/ids';

const NOTION_LINK_RE = /\[([^\]]*)\]\(([^)]+)\)/gi;

export type ImportKindMap = Record<string, 'page' | 'database'>;

function basenameFromHref(href: string): string {
  return normalizeExportPath(decodeURIComponent(href)).split('/').pop() ?? href;
}

function notionIdFromHref(href: string): string | null {
  const base = basenameFromHref(href);
  if (!/\.(md|csv)$/i.test(base)) {
    return null;
  }
  return extractNotionIdFromFilename(base);
}

/**
 * Remove markdown links to child pages/databases that are imported as block
 * children of this page. Notion's export duplicates the tree as inline links
 * in the parent .md; we already place those items under the page in the block
 * tree, so keeping the links creates redundant "Add file" noise or stray text.
 */
export function stripImportedChildLinks(markdown: string, childNotionIds: Set<string>): string {
  if (childNotionIds.size === 0) {
    return markdown;
  }
  return markdown.replace(NOTION_LINK_RE, (match, _label: string, href: string) => {
    const notionId = notionIdFromHref(href);
    if (notionId && childNotionIds.has(notionId)) {
      return '';
    }
    return match;
  });
}

export function childNotionIdsByParent(
  plan: Array<{ notionId: string; parentNotionId: string | null }>,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const item of plan) {
    if (!item.parentNotionId) {
      continue;
    }
    const set = map.get(item.parentNotionId) ?? new Set<string>();
    set.add(item.notionId);
    map.set(item.parentNotionId, set);
  }
  return map;
}

function routeForNotionId(
  notionId: string,
  blockId: string,
  kindMap: ImportKindMap,
): string | null {
  const kind = kindMap[notionId] ?? 'page';
  return kind === 'database' ? `/database/${blockId}` : `/page/${blockId}`;
}

export function rewriteNotionLinksInMarkdown(
  markdown: string,
  idMap: Record<string, string>,
  kindMap: ImportKindMap = {},
): string {
  return markdown.replace(NOTION_LINK_RE, (match, label: string, href: string) => {
    const base = basenameFromHref(href);
    if (!/\.(md|csv)$/i.test(base)) {
      return match;
    }
    const notionId = extractNotionIdFromFilename(base);
    if (!notionId || !idMap[notionId]) {
      return match;
    }
    const route = routeForNotionId(notionId, idMap[notionId]!, kindMap);
    return route ? `[${label}](${route})` : match;
  });
}

export function buildImportKindMap(
  plan: Array<{ notionId: string; kind: 'page' | 'database' }>,
): ImportKindMap {
  const kindMap: ImportKindMap = {};
  for (const item of plan) {
    kindMap[item.notionId] = item.kind;
  }
  return kindMap;
}

/** Pre-assign block ids for every planned import item so link rewriting works across batches. */
export function buildPlannedIdMap(
  plan: Array<{ notionId: string }>,
): Record<string, string> {
  const idMap: Record<string, string> = {};
  for (const item of plan) {
    idMap[item.notionId] = newBlockId();
  }
  return idMap;
}
